import {ResultColumn} from "./reportApi";
import {toNumber} from "./chartShape";

/**
 * A chart as a SPECIFICATION, and the table that decides what each kind can do.
 *
 * THE SAME MOVE THE WHOLE APP RESTS ON. A report is a structured document the server compiles --
 * nobody writes SQL, the identifiers come from a catalogue and the operators from dispatch tables.
 * A chart is that applied one layer up: a document saying which column is the category, which are
 * the values, and how to draw them. Rendering is a function from the document to MUI props; the
 * builder is a UI that edits the document and knows nothing about MUI.
 *
 * ONE TABLE DRIVES THREE THINGS that used to be three pieces of code:
 *
 *   which chart buttons are offered          -- CAPABILITIES[].slots, checked against the result
 *   what the refusal says when one is not    -- the same check, reported instead of hidden
 *   which shelves the builder shows          -- the same slots, rendered
 *
 * Before this, `chartRefusal` had a branch per kind and the mapping from result to chart was
 * implicit: first dimension is the axis, every measure is a series. That default is right often
 * enough to have carried the feature, and it is a ceiling -- you could not choose the axis, plot one
 * measure against another, or reach any chart the library has beyond three.
 *
 * NOT TAKEN FROM t1: `CCChart`, the base class under the CC* chart family there. Its
 * componentDidMount registers fifteen listeners on `props.applet.getEnterpriseComponent()`, and a
 * report chart has no EnterpriseComponent -- its rows are plain arrays the store returned. Rendered
 * without an applet it throws, which Playwright caught the first time anybody pressed Bar. TAKEN
 * from t1: the idea in `CCMuiChart` that a chart is configured by a plain object rather than by JSX.
 */

export type ChartKind = "bar" | "line" | "pie" | "scatter" | "gauge" | "heatmap"
                      | "radar" | "funnel" | "sparkline" | "sankey";

/** What a column has to be to sit in a slot. */
export type SlotAccepts = "dimension" | "measure";

export type ChartSlot = {
    id: string;
    /** What the shelf is called in the builder. */
    label: string;
    accepts: SlotAccepts;
    /** More than one column may sit here. */
    multiple?: boolean;
    required: boolean;
    /** Shown under the shelf when it is empty. */
    hint?: string;
};

/** The id of a switch this control depends on. Shown either way, greyed when that switch is off:
 *  a control that vanishes is one nobody finds again, and a live-looking one that changes nothing
 *  is worse than either. */
type DependsOn = {enabledWhen?: string};

export type ChartOption =
    | {id: string; label: string; kind: "switch"; default: boolean}
    | ({id: string; label: string; kind: "choice"; default: string;
        choices: {id: string; label: string}[]} & DependsOn)
    // A PROPORTION, NOT A PIXEL COUNT. The two gap settings MUI takes are ratios of the band, so
    // they survive the chart being resized -- which every chart here is, by its tile.
    | ({id: string; label: string; kind: "number"; default: number;
        min: number; max: number; step: number} & DependsOn);

export type ChartCapability = {
    kind: ChartKind;
    label: string;
    /** One line, shown beside the chart type so the choice is informed rather than guessed. */
    about: string;
    slots: ChartSlot[];
    options: ChartOption[];
    /**
     * A rule the slots cannot express, reported the same way. Returns the reason or null.
     * A pie of negatives is the reason this exists: slices are shares of a whole, a negative share
     * has no area, and every library draws something anyway -- what it draws is a lie.
     */
    check?: (spec: ChartSpec, columns: ResultColumn[], rows: unknown[][]) => string | null;
    /**
     * This chart is a category axis with one series per measure, so the reader can tune its axes
     * and each of its series. Bar and line, and nothing else: a pie has no axes, a scatter is one
     * series by construction, and a heatmap's third dimension is colour rather than a scale.
     */
    tunable?: boolean;
};

/**
 * WHICH VALUE AXIS A SERIES IS MEASURED AGAINST.
 *
 * NAMED FOR ITS ROLE, NOT ITS SIDE, because the side depends on the layout: on a vertical chart
 * these are the left and right y axes, and on a bar chart laid on its side they are the bottom and
 * top x axes. A specification that said "left" would be wrong the moment somebody pressed "Lay it
 * on its side", and it is the same specification either way.
 *
 * WHAT IT IS FOR: two measures of different magnitude on one chart -- four thousand orders beside
 * two million dollars of revenue. On one scale the smaller series is a flat line along the floor.
 *
 * AND WHAT IT COSTS, said here because nobody reading the chart is told: a second scale means the
 * two series can be made to cross wherever the scales are chosen to make them cross, so an apparent
 * relationship between them is a property of the axes rather than of the data. Use it for series a
 * reader will compare in SHAPE, and not to imply that one causes the other.
 */
export type SeriesAxis = "primary" | "secondary";

/** How one measure is drawn, when that differs from the chart it is on. */
export type SeriesDrawing = "bar" | "line";

/** One measure's own settings, keyed in the specification by its column Key. */
export type SeriesSettings = {
    axis?: SeriesAxis;
    /**
     * BARS OR A LINE, INDEPENDENTLY OF THE CHART. Absent means "whatever the chart is", which is
     * what every specification saved before this says.
     *
     * THIS IS WHAT A SECOND AXIS IS USUALLY FOR. Volume as bars with a rate drawn over them is the
     * shape people mean by a combination chart, and the reason is legibility rather than decoration:
     * two series drawn the same way at different magnitudes read as one series and a mistake, and
     * giving one of them a different MARK says "these are different kinds of thing" before the
     * reader has looked at either axis.
     */
    draw?: SeriesDrawing;
    /** An explicit colour, overriding the palette's order. Absent = whatever position it holds. */
    color?: string;
    /** Line only. Absent means "whatever the chart says" -- which is why these are optional
     *  booleans rather than plain ones: `false` is a deliberate OFF against a chart that is on,
     *  and `undefined` is "do not decide here". A plain switch cannot express all three, so the
     *  builder offers them as a three-way choice. */
    showMark?: boolean;
    /** Fill the band beneath this line. */
    area?: boolean;
    /** Join across a missing value instead of leaving the gap. */
    connectNulls?: boolean;
    /** Line only: this line's shape, overriding the chart's. */
    curve?: string;
};

/** One axis's own settings. Every field absent means "as the data and the library decide". */
export type AxisSettings = {
    label?: string;
    /** A FIXED FLOOR AND CEILING. Left out, MUI picks a round range from the data -- which is
     *  right until two charts meant to be compared pick different ones. */
    min?: number;
    max?: number;
    scale?: "linear" | "log" | "sqrt";
    /** Largest to smallest rather than smallest to largest. */
    reverse?: boolean;
    /** Degrees, the category axis only: what saves forty long labels from overlapping. */
    tickAngle?: number;
};

export type AxisId = "category" | "primary" | "secondary";

export type ChartSpec = {
    version: 1;
    kind: ChartKind;
    /** Slot id -> column Keys. Always an array, even for a single-column slot, so that the builder
     *  and the compiler have one shape to handle rather than two. */
    encodings: Record<string, string[]>;
    options: Record<string, boolean | string | number>;
    /**
     * Per-measure settings, by column Key -- and OPTIONAL, like `axes` below.
     *
     * EVERY SPECIFICATION EVER SAVED IS STILL VALID. These are stored on reports and on dashboard
     * tiles as opaque documents the server does not read, so a spec written before this existed
     * arrives without them and must draw exactly as it did: absent means "the default", never
     * "unset, so use nothing". Keyed by COLUMN KEY rather than by position, so reordering a shelf
     * does not hand one measure another's colour.
     */
    series?: Record<string, SeriesSettings>;
    axes?: Partial<Record<AxisId, AxisSettings>>;
};

// ---------------------------------------------------------------------------------------------
// The table.
// ---------------------------------------------------------------------------------------------

/** How many distinct values a heatmap axis can carry and still be read. A judgement, not a limit of
 *  the library -- which is why the refusal says the number instead of just refusing. */
export const HEATMAP_MOST_CELLS = 50;

/** The fewest axes a radar is still a radar on. Two metrics draw a line and one draws a spoke;
 *  both are shapes the reader would have to be told not to interpret as area. */
export const RADAR_FEWEST_AXES = 3;

/** And the most. Past this the spokes are closer together than their own labels, which is the
 *  radar version of an axis truncating to nothing. */
export const RADAR_MOST_AXES = 12;

export const CAPABILITIES: ChartCapability[] = [
    {
        kind: "bar",
        label: "Bar",
        about: "Compares a measure across the things you grouped by.",
        slots: [
            {id: "category", label: "Category", accepts: "dimension", required: true,
             hint: "the axis"},
            {id: "values", label: "Values", accepts: "measure", required: true, multiple: true,
             hint: "one bar series each"},
        ],
        options: [
            {id: "stack", label: "Stack the series", kind: "switch", default: false},
            // ONLY MEANS ANYTHING STACKED. Shown either way and greyed when it is not -- a
            // control that appears and disappears as another is touched is harder to find again,
            // and one that looks live while doing nothing was what this actually rendered until
            // somebody looked at a screenshot of it.
            {id: "stackMode", label: "Stacked as", kind: "choice", default: "none",
             enabledWhen: "stack", choices: [
                {id: "none", label: "Totals"},
                {id: "expand", label: "Shares of 100%"},
                {id: "diverging", label: "Above and below zero"},
            ]},
            {id: "horizontal", label: "Lay it on its side", kind: "switch", default: false},
            {id: "hideLegend", label: "Hide the legend", kind: "switch", default: false},
            {id: "grid", label: "Grid lines", kind: "choice", default: "horizontal", choices: [
                {id: "none", label: "None"},
                {id: "horizontal", label: "Horizontal"},
                {id: "vertical", label: "Vertical"},
                {id: "both", label: "Both"},
            ]},
            {id: "legendPosition", label: "Legend", kind: "choice", default: "bottom", choices: [
                {id: "bottom", label: "Below"},
                {id: "top", label: "Above"},
                {id: "start", label: "Left"},
                {id: "end", label: "Right"},
            ]},
            // RATIOS OF THE BAND, so they hold when the tile is resized. 0 puts bars edge to
            // edge; the library's own defaults are 0.1 and 0.
            {id: "categoryGap", label: "Gap between groups", kind: "number", default: 0.1,
             min: 0, max: 0.9, step: 0.05},
            // ONE BAR PER GROUP HAS NOTHING TO GAP AGAINST, but this one depends on how many
            // measures are on the shelf rather than on another switch, so it stays live.
            {id: "barGap", label: "Gap within a group", kind: "number", default: 0,
             min: 0, max: 0.9, step: 0.05},
        ],
        tunable: true,
    },
    {
        kind: "line",
        label: "Line",
        about: "Shows how a measure moves across an ordered category.",
        slots: [
            {id: "category", label: "Category", accepts: "dimension", required: true,
             hint: "the axis"},
            {id: "values", label: "Values", accepts: "measure", required: true, multiple: true,
             hint: "one line each"},
        ],
        options: [
            {id: "area", label: "Fill beneath the line", kind: "switch", default: false},
            {id: "curve", label: "Shape", kind: "choice", default: "monotone", choices: [
                {id: "monotone", label: "Curved"},
                {id: "linear", label: "Straight"},
                {id: "step", label: "Stepped"},
            ]},
            {id: "showMark", label: "Mark each point", kind: "switch", default: false},
            // A GAP IS A GAP UNLESS YOU SAY OTHERWISE. Joining across a missing month draws a
            // line through data that was never there, so this is off by default and named for
            // what it does rather than for the library's word.
            {id: "connectNulls", label: "Join across gaps", kind: "switch", default: false},
            {id: "hideLegend", label: "Hide the legend", kind: "switch", default: false},
            {id: "grid", label: "Grid lines", kind: "choice", default: "horizontal", choices: [
                {id: "none", label: "None"},
                {id: "horizontal", label: "Horizontal"},
                {id: "vertical", label: "Vertical"},
                {id: "both", label: "Both"},
            ]},
            {id: "legendPosition", label: "Legend", kind: "choice", default: "bottom", choices: [
                {id: "bottom", label: "Below"},
                {id: "top", label: "Above"},
                {id: "start", label: "Left"},
                {id: "end", label: "Right"},
            ]},
        ],
        tunable: true,
    },
    {
        kind: "pie",
        label: "Pie",
        about: "Shows one measure as shares of a whole.",
        slots: [
            {id: "category", label: "Slices", accepts: "dimension", required: true,
             hint: "one slice each"},
            {id: "values", label: "Value", accepts: "measure", required: true,
             hint: "the size of each slice"},
        ],
        options: [
            {id: "donut", label: "Leave the middle open", kind: "switch", default: false},
            {id: "hideLegend", label: "Hide the legend", kind: "switch", default: false},
        ],
        check: (spec, columns, rows) => {
            const at = indexOfSlot(spec, columns, "values");
            if (at < 0) return null;
            return rows.some(row => (toNumber(row[at]) ?? 0) < 0)
                ? "a pie cannot show negative values" : null;
        },
    },
    {
        kind: "scatter",
        label: "Scatter",
        about: "Plots one measure against another, a point per row.",
        slots: [
            {id: "x", label: "Across", accepts: "measure", required: true, hint: "the x axis"},
            {id: "y", label: "Up", accepts: "measure", required: true, hint: "the y axis"},
            {id: "label", label: "Label", accepts: "dimension", required: false,
             hint: "optional — names each point"},
        ],
        options: [
            {id: "hideLegend", label: "Hide the legend", kind: "switch", default: true},
            {id: "grid", label: "Grid lines", kind: "choice", default: "horizontal", choices: [
                {id: "none", label: "None"},
                {id: "horizontal", label: "Horizontal"},
                {id: "vertical", label: "Vertical"},
                {id: "both", label: "Both"},
            ]},
            {id: "legendPosition", label: "Legend", kind: "choice", default: "bottom", choices: [
                {id: "bottom", label: "Below"},
                {id: "top", label: "Above"},
                {id: "start", label: "Left"},
                {id: "end", label: "Right"},
            ]},
        ],
    },
    {
        kind: "heatmap",
        label: "Heatmap",
        about: "Shows one measure as colour, across two things you grouped by.",
        slots: [
            {id: "x", label: "Across", accepts: "dimension", required: true, hint: "the columns"},
            {id: "y", label: "Down", accepts: "dimension", required: true, hint: "the rows"},
            {id: "values", label: "Value", accepts: "measure", required: true,
             hint: "the colour of each cell"},
        ],
        options: [
            {id: "hideLegend", label: "Hide the legend", kind: "switch", default: false},
        ],
        // THE FIRST CHART HERE THAT NEEDS TWO DIMENSIONS, and the two rules below are what that
        // costs. Both are stated rather than drawn around, the way the pie refuses negatives.
        check: (spec, columns, rows) => {
            const xAt = indexOfSlot(spec, columns, "x");
            const yAt = indexOfSlot(spec, columns, "y");
            if (xAt < 0 || yAt < 0) return null;

            // ONE VALUE PER CELL, OR IT IS NOT A HEATMAP. A grid has one colour per square, so two
            // rows landing on the same square means one of them is not shown -- and the reader
            // cannot tell which, or that it happened. This is the shape of a report grouped by
            // something the chart does not use: Department x Month summed, with Status still in the
            // result. Refusing names it; drawing it would be the pie-of-negatives mistake.
            const seen = new Set<string>();
            for (const row of rows) {
                // \u0000 as the join, because "a" + "b|c" and "a|b" + "c" are different cells and a
                // printable separator makes them the same string.
                const cell = `${String(row[xAt] ?? "")}\u0000${String(row[yAt] ?? "")}`;
                if (seen.has(cell)) {
                    return "has more than one row per cell, so a heatmap would draw one and hide "
                         + "the rest -- group it by just these two";
                }
                seen.add(cell);
            }

            // AND IT HAS TO BE LEGIBLE. Cells shrink with the grid while the labels do not: past
            // this many the axis truncates to nothing and the squares go sub-pixel, which is a
            // picture that cannot be read rather than one that is wrong. The number is said out
            // loud so the answer is "group it more coarsely", not "the chart is broken".
            const across = new Set(rows.map(row => String(row[xAt] ?? ""))).size;
            const down = new Set(rows.map(row => String(row[yAt] ?? ""))).size;
            const most = Math.max(across, down);
            return most > HEATMAP_MOST_CELLS
                ? `has ${most} values on one side; a heatmap stays readable to about `
                  + `${HEATMAP_MOST_CELLS}`
                : null;
        },
    },
    {
        kind: "radar",
        label: "Radar",
        about: "Compares several measures over the same things, as overlaid shapes.",
        slots: [
            {id: "category", label: "Axes", accepts: "dimension", required: true,
             hint: "one spoke each"},
            {id: "values", label: "Values", accepts: "measure", required: true, multiple: true,
             hint: "one shape each"},
        ],
        options: [
            {id: "fillArea", label: "Fill the shapes", kind: "switch", default: true},
            {id: "hideMark", label: "Hide the points", kind: "switch", default: false},
            {id: "hideLegend", label: "Hide the legend", kind: "switch", default: false},
        ],
        // THE ROWS ARE THE AXES HERE, which is the transpose of every other chart in this table:
        // a bar chart grows a bar per row, a radar grows a SPOKE per row and a shape per measure.
        // Both rules below are about the rows for that reason.
        check: (spec, columns, rows) => {
            if (rows.length < RADAR_FEWEST_AXES) {
                return `has ${rows.length} row${rows.length === 1 ? "" : "s"}; a radar needs at `
                     + `least ${RADAR_FEWEST_AXES} to enclose an area`;
            }
            if (rows.length > RADAR_MOST_AXES) {
                return `has ${rows.length} rows; a radar stays readable to about `
                     + `${RADAR_MOST_AXES} spokes`;
            }
            // A POLYGON HAS TO CLOSE. Every other chart here leaves an unreadable value as a gap,
            // which a bar or a line simply omits. A radar cannot: the shape joins every axis in
            // turn, so a missing value is drawn as a spike to the centre -- nought, asserted.
            for (const at of indexesOfSlot(spec, columns, "values")) {
                if (rows.some(row => toNumber(row[at]) === null)) {
                    return `has no number for ${columns[at]?.Label ?? "a value"} on every axis, `
                         + "and a radar shape cannot leave one out";
                }
            }
            return null;
        },
    },
    {
        kind: "funnel",
        label: "Funnel",
        about: "Shows a measure falling away through stages, in the order the rows are in.",
        slots: [
            {id: "category", label: "Stages", accepts: "dimension", required: true,
             hint: "in the order they happen"},
            {id: "values", label: "Value", accepts: "measure", required: true,
             hint: "how many reach each stage"},
        ],
        options: [
            {id: "shape", label: "Shape", kind: "choice", default: "linear", choices: [
                {id: "linear", label: "Straight"},
                {id: "pyramid", label: "Pyramid"},
                {id: "step", label: "Stepped"},
            ]},
            {id: "horizontal", label: "Lay it on its side", kind: "switch", default: false},
            {id: "hideLegend", label: "Hide the legend", kind: "switch", default: false},
        ],
        check: (spec, columns, rows) => {
            if (rows.length < 2) return "has one stage; a funnel is about what happens between them";
            const at = indexOfSlot(spec, columns, "values");
            if (at < 0) return null;
            // THE PIE'S RULE, FOR THE SAME REASON. A section's size IS its value, so a negative
            // one has no area -- and the library draws something anyway.
            return rows.some(row => (toNumber(row[at]) ?? 0) < 0)
                ? "a funnel cannot show negative values" : null;
        },
    },
    {
        kind: "sparkline",
        label: "Sparkline",
        about: "The shape of one measure, small and without axes. For a tile, not a page.",
        slots: [
            {id: "values", label: "Value", accepts: "measure", required: true,
             hint: "the line to draw"},
            {id: "category", label: "Label", accepts: "dimension", required: false,
             hint: "optional — names each point in the tooltip"},
        ],
        options: [
            {id: "shape", label: "Shape", kind: "choice", default: "line", choices: [
                {id: "line", label: "Line"},
                {id: "bar", label: "Bars"},
            ]},
            {id: "area", label: "Fill beneath the line", kind: "switch", default: true},
        ],
        // NO LEGEND OPTION, because there is no legend: one series needs no key, and the tile's
        // own heading already names what is being drawn.
        check: (_spec, _columns, rows) =>
            rows.length < 2 ? "has one row; a sparkline is a shape, which needs at least two"
                            : null,
    },
    {
        kind: "sankey",
        label: "Sankey",
        about: "Shows how a measure flows from one thing to another.",
        slots: [
            {id: "x", label: "From", accepts: "dimension", required: true, hint: "where it starts"},
            {id: "y", label: "To", accepts: "dimension", required: true, hint: "where it goes"},
            {id: "values", label: "Value", accepts: "measure", required: true,
             hint: "the width of the flow"},
        ],
        options: [
            {id: "showLabels", label: "Name the nodes", kind: "switch", default: true},
            {id: "align", label: "Line up", kind: "choice", default: "justify", choices: [
                {id: "justify", label: "Across the width"},
                {id: "left", label: "To the left"},
                {id: "right", label: "To the right"},
                {id: "center", label: "In the middle"},
            ]},
        ],
        // THE SAME TWO DIMENSIONS AND A MEASURE AS THE HEATMAP, and an entirely different set of
        // rules -- because a sankey is a GRAPH and the other charts are grids. All three below are
        // things the layout cannot resolve rather than preferences.
        check: (spec, columns, rows) => {
            const fromAt = indexOfSlot(spec, columns, "x");
            const toAt = indexOfSlot(spec, columns, "y");
            const at = indexOfSlot(spec, columns, "values");
            if (fromAt < 0 || toAt < 0) return null;

            const from = (row: unknown[]) => String(row[fromAt] ?? "");
            const to = (row: unknown[]) => String(row[toAt] ?? "");

            // A FLOW FROM SOMETHING TO ITSELF HAS NOWHERE TO GO. The layout puts nodes in columns
            // by how far along the flow they are, and a self-link asks for a node to be both
            // before and after itself.
            const loop = rows.find(row => from(row) === to(row));
            if (loop) {
                return `has ${from(loop)} flowing to itself, which a sankey has nowhere to draw`;
            }

            // NOR CAN IT GO ROUND. The same rule one step out: A to B and B back to A leaves the
            // layout with no order to put the columns in. Walked properly rather than checked
            // pairwise, because a cycle of three is just as unresolvable as a cycle of two and
            // reads exactly as plausible in the data.
            const edges = new Map<string, Set<string>>();
            for (const row of rows) {
                if (!edges.has(from(row))) edges.set(from(row), new Set());
                edges.get(from(row))!.add(to(row));
            }
            const done = new Set<string>(), open = new Set<string>();
            let cycle: string | null = null;
            const walk = (node: string) => {
                if (cycle || done.has(node)) return;
                if (open.has(node)) { cycle = node; return; }
                open.add(node);
                for (const next of edges.get(node) ?? []) walk(next);
                open.delete(node);
                done.add(node);
            };
            for (const node of edges.keys()) walk(node);
            if (cycle) {
                return `flows in a circle through ${cycle}, and a sankey has to run one way`;
            }

            if (at < 0) return null;
            // A NEGATIVE FLOW HAS NO WIDTH, which is the pie's rule for the pie's reason.
            return rows.some(row => (toNumber(row[at]) ?? 0) < 0)
                ? "a sankey cannot show negative values" : null;
        },
    },
    {
        kind: "gauge",
        label: "Gauge",
        about: "One number, against a range. For a result with a single row.",
        slots: [
            {id: "values", label: "Value", accepts: "measure", required: true,
             hint: "the number to show"},
        ],
        options: [],
        check: (_spec, _columns, rows) =>
            rows.length === 1 ? null
                : `a gauge shows one number; this result has ${rows.length} rows`,
    },
];

export function capabilityOf(kind: ChartKind): ChartCapability {
    // NEVER undefined: an unknown kind in a stored specification falls back to the one every result
    // that can be charted at all can support, rather than rendering nothing.
    return CAPABILITIES.find(one => one.kind === kind) ?? CAPABILITIES[0];
}

// ---------------------------------------------------------------------------------------------
// Reading a specification against a result.
// ---------------------------------------------------------------------------------------------

function columnsFor(accepts: SlotAccepts, columns: ResultColumn[]) {
    return columns.filter(one => (one.Role ?? "dimension") === accepts);
}

/** The column index a single-column slot points at, or -1. */
export function indexOfSlot(spec: ChartSpec, columns: ResultColumn[], slot: string): number {
    const key = (spec.encodings?.[slot] ?? [])[0];
    return key ? columns.findIndex(one => one.Key === key) : -1;
}

/** Every column index a slot points at, in the order the reader put them. */
export function indexesOfSlot(spec: ChartSpec, columns: ResultColumn[], slot: string): number[] {
    return (spec.encodings?.[slot] ?? [])
        .map(key => columns.findIndex(one => one.Key === key))
        .filter(index => index >= 0);
}

/**
 * A specification that draws this result with this chart, chosen the way the old implicit mapping
 * chose: first dimension as the category, every measure as a value.
 *
 * THE OLD BEHAVIOUR, WRITTEN DOWN. That mapping used to be buried in `chartData`; as a default it
 * is a good guess, and as the ONLY option it was the ceiling. Now it is where a reader starts.
 */
export function defaultSpec(kind: ChartKind, columns: ResultColumn[]): ChartSpec {
    const capability = capabilityOf(kind);
    const available = {dimension: columnsFor("dimension", columns),
                       measure: columnsFor("measure", columns)};

    // TAKEN IN TURN, PER KIND OF SHELF. Measures were already consumed in order; dimensions used to
    // be handed `dimensions[0]` every time, which was invisible while no chart declared two of them
    // and would default a heatmap to one column crossed with itself -- a single stripe, from a
    // default that looks deliberate.
    const taken = {dimension: 0, measure: 0};

    const encodings: Record<string, string[]> = {};
    for (const slot of capability.slots) {
        const pool = available[slot.accepts];
        if (slot.multiple) {
            encodings[slot.id] = pool.slice(taken[slot.accepts]).map(one => one.Key);
            taken[slot.accepts] = pool.length;
        } else {
            const one = pool[taken[slot.accepts]];
            encodings[slot.id] = one ? [one.Key] : [];
            if (one) taken[slot.accepts] += 1;
        }
    }

    const options: Record<string, boolean | string | number> = {};
    for (const option of capability.options) options[option.id] = option.default;

    // NO `series` AND NO `axes`. A default specification carries no tuning at all, so the chart a
    // reader gets before touching anything is exactly the chart this app drew before any of it
    // existed -- and `undefined` reads the same as a spec saved a month ago.
    return {version: 1, kind, encodings, options};
}

// ---------------------------------------------------------------------------------------------
// Tuning: per-series and per-axis settings, and the MUI props they compile to.
//
// PURE, AND HERE RATHER THAN IN THE RENDERER, for the reason the rest of this file exists: the
// renderer is a JSX function Jest cannot mount without the chart library, and every rule below is
// a decision worth a test. ReportChart calls these and puts the result on a component.
// ---------------------------------------------------------------------------------------------

/** One measure's settings, or the empty set. Never undefined, so callers do not each guard. */
export function seriesSettingsOf(spec: ChartSpec, key: string): SeriesSettings {
    return spec.series?.[key] ?? {};
}

export function axisSettingsOf(spec: ChartSpec, id: AxisId): AxisSettings {
    return spec.axes?.[id] ?? {};
}

/** Which axis a measure is measured against. Anything unset is the primary one. */
export function axisOf(spec: ChartSpec, key: string): SeriesAxis {
    return seriesSettingsOf(spec, key).axis === "secondary" ? "secondary" : "primary";
}

/**
 * How one measure is drawn.
 *
 * `sideways` IS NOT A STYLE QUESTION, IT IS A CONSTRAINT. MUI has no horizontal line series -- a
 * line goes left to right over a banded x axis and there is no other form of it -- so on a bar
 * chart laid on its side every measure is a bar whatever it was set to. The SETTING IS KEPT rather
 * than cleared, so turning the chart upright restores the combination the reader built; it is
 * overruled here, at the one place that reads it, and the builder greys the control and says why.
 */
export function drawnAs(spec: ChartSpec, key: string, sideways = false): SeriesDrawing {
    if (sideways) return "bar";
    const own = seriesSettingsOf(spec, key).draw;
    if (own === "bar" || own === "line") return own;
    return spec.kind === "line" ? "line" : "bar";
}

/**
 * The one kind every measure is drawn as, or null when they differ -- which is a combination.
 *
 * WHY A SINGLE KIND IS WORTH DETECTING: a chart whose every series ended up a line is a line chart,
 * and `LineChartPro` draws it with the library's own composition rather than this app's. The
 * hand-composed path exists for the case the ready-made components cannot express, and nothing
 * else should be routed through it.
 */
export function singleDrawing(spec: ChartSpec, keys: string[],
                              sideways = false): SeriesDrawing | null {
    if (keys.length === 0) return spec.kind === "line" ? "line" : "bar";
    const kinds = new Set(keys.map(key => drawnAs(spec, key, sideways)));
    return kinds.size === 1 ? [...kinds][0] : null;
}

/**
 * Whether a second value axis is drawn at all.
 *
 * ONLY WHEN SOMETHING IS ON IT. An empty axis is a labelled line down the side of the chart with
 * no meaning, and it steals width from the plot -- so the axis exists because a series put it
 * there, not because the specification has a slot for one.
 */
export function usesSecondaryAxis(spec: ChartSpec, keys: string[]): boolean {
    return keys.some(key => axisOf(spec, key) === "secondary");
}

/**
 * A value axis as MUI takes it.
 *
 * `tickMinStep` IS THE OLD BEHAVIOUR AND IT STAYS: a count of three does not have tenths, so an
 * axis whose values are all whole numbers steps by one. It is passed only when the reader has not
 * fixed the range themselves -- once there is a min and a max, the step is theirs to imply.
 */
export function valueAxisProps(settings: AxisSettings, wholeNumbers: boolean): Record<string, any> {
    const out: Record<string, any> = {};
    if (settings.label) out.label = settings.label;
    if (typeof settings.min === "number") out.min = settings.min;
    if (typeof settings.max === "number") out.max = settings.max;
    if (settings.reverse) out.reverse = true;

    // A LOG AXIS CANNOT SHOW ZERO OR A NEGATIVE -- log(0) is not a number, and every library
    // silently drops the point or draws it off the canvas. Asked for on data that has one, the
    // scale is ignored rather than obeyed, because a chart missing a bar without saying so is
    // worse than a chart on the scale it was already on. The builder says so beside the control.
    if (settings.scale === "log" || settings.scale === "sqrt") out.scaleType = settings.scale;

    if (wholeNumbers && out.min === undefined && out.max === undefined) out.tickMinStep = 1;
    return out;
}

/** The category axis, which has no scale of its own -- only what its labels do. */
export function categoryAxisProps(settings: AxisSettings): Record<string, any> {
    const out: Record<string, any> = {};
    if (settings.label) out.label = settings.label;
    if (settings.reverse) out.reverse = true;
    if (settings.tickAngle) {
        out.tickLabelStyle = {angle: settings.tickAngle,
                              // ANCHORED AT THE END WHEN TILTED, or a rotated label pivots about
                              // its middle and drifts away from the tick it names.
                              textAnchor: settings.tickAngle > 0 ? "start" : "end"};
    }
    return out;
}

/** Where the legend sits, as MUI's two-part position. */
export function legendPosition(option: unknown): {vertical: "top" | "middle" | "bottom";
                                                  horizontal: "start" | "center" | "end"} {
    switch (option) {
        case "top": return {vertical: "top", horizontal: "center"};
        case "start": return {vertical: "middle", horizontal: "start"};
        case "end": return {vertical: "middle", horizontal: "end"};
        default: return {vertical: "bottom", horizontal: "center"};
    }
}

/** Grid lines, from one control rather than two booleans. */
export function gridProps(option: unknown): {horizontal?: boolean; vertical?: boolean} | undefined {
    switch (option) {
        case "none": return undefined;
        case "vertical": return {vertical: true};
        case "both": return {horizontal: true, vertical: true};
        case "horizontal": return {horizontal: true};
        // UNDEFINED IS NOT "NONE": a specification saved before this option existed has no value
        // here, and it drew with the library's default. Horizontal is that default made explicit.
        default: return {horizontal: true};
    }
}

export type ChartRefusal = {why: string};

/**
 * Why this specification cannot be drawn, or null.
 *
 * THE ANSWER IS "WHY NOT", NEVER A BLANK PANEL. Every chart tool has the failure where you pick a
 * type and get an empty rectangle with no explanation. The reason is data here, composed from the
 * slots the chart declares, so a new chart type gets good refusals without writing any.
 */
export function refuse(spec: ChartSpec, columns: ResultColumn[],
                       rows: unknown[][]): ChartRefusal | null {
    if (!columns.length || !rows.length) return {why: "Run the report first"};

    const capability = capabilityOf(spec.kind);

    // WHAT THE RESULT LACKS COMES FIRST, in one pass over every required slot, before what the
    // READER has not filled in. The two orders only differ once a chart declares two slots of one
    // kind: a heatmap asked of a result with no measure at all would otherwise answer "needs a
    // column on down", sending somebody to a shelf that cannot be filled either. The shape of the
    // result is the reason, and the shelf is merely where they would have noticed.
    for (const slot of capability.slots) {
        if (!slot.required) continue;
        if (!columnsFor(slot.accepts, columns).length) {
            return {why: slot.accepts === "dimension"
                ? "needs something to group by"
                : "needs something summarized"};
        }
    }

    for (const slot of capability.slots) {
        if (!slot.required) continue;
        if (!indexesOfSlot(spec, columns, slot.id).length) {
            return {why: `needs a column on ${slot.label.toLowerCase()}`};
        }
    }

    // CALLED ONCE. Calling it in the condition and again for the value runs a rule over every row
    // twice, and invites the two calls to disagree the day a check stops being pure.
    const why = capability.check?.(spec, columns, rows) ?? null;
    return why ? {why} : null;
}

/**
 * Which chart kinds this result could support AT ALL, each with a reason when it cannot.
 *
 * ASKED OF THE DEFAULT SPECIFICATION, not of whatever the reader has built: the question the chart
 * strip answers is "could I switch to this", and answering it from a half-built specification would
 * grey out a chart because of a choice the reader has not made yet.
 */
export function availableCharts(columns: ResultColumn[], rows: unknown[][]) {
    return CAPABILITIES.map(capability => ({
        kind: capability.kind,
        label: capability.label,
        about: capability.about,
        refusal: refuse(defaultSpec(capability.kind, columns), columns, rows),
    }));
}


/**
 * Which chart a dashboard tile actually draws.
 *
 * ONE PLACE, because the precedence IS the feature: a tile's own chart, else the report's, else the
 * default for the tile's kind -- which is the implicit mapping this always had, so a tile nobody
 * has touched draws exactly as it did before charts were stored at all.
 *
 * HERE RATHER THAN IN dashboardApi, where it started: that module imports axios, so anything in it
 * drags an ESM package into Jest and the whole suite fails to load. A pure function belongs beside
 * the other pure functions.
 */
export function chartForTile(tile: {kind?: string; ChartKind?: string;
                                    ChartSpecification?: ChartSpec | null;
                                    TileChartSpecification?: ChartSpec | null},
                             columns: ResultColumn[]): ChartSpec {
    return tile.TileChartSpecification
        ?? tile.ChartSpecification
        ?? defaultSpec(defaultKindFor(tile.ChartKind ?? tile.kind), columns);
}

/**
 * What a dashboard tile does with its report: show the rows, or draw them.
 *
 * TWO VALUES, BECAUSE THAT IS ALL THE TILE DECIDES. The field was once the chart TYPE, and the
 * picker still offered the four types it knew -- which had become a lie in both directions: a tile
 * could not be saved as a scatter or a gauge, and a tile saved as "bar" drew a gauge whenever that
 * was the chart. Which chart is drawn belongs to the SPECIFICATION, which is the one place that
 * knows what the result can support and what to say when it cannot.
 */
export type TileDrawing = "table" | "chart";

/** How a stored tile kind reads now. Anything that is not the table is a picture of it, INCLUDING
 *  the chart names tiles were saved under before the collapse. */
export function drawingOf(stored: string | null | undefined): TileDrawing {
    return (stored ?? "table") === "table" ? "table" : "chart";
}

/**
 * The chart a drawn tile falls back to when no specification exists anywhere.
 *
 * A LEGACY NAME IS STILL HONOURED. Tiles saved before charts were stored hold "bar", "line" or
 * "pie", and that name is the only record of how such a tile has always drawn -- reading it as a
 * plain "chart" and defaulting to bar would silently change deployed dashboards. A name no chart
 * answers to (including "chart" and "table") falls back to bar, which is what the implicit mapping
 * always produced.
 */
function defaultKindFor(stored: string | undefined): ChartKind {
    return CAPABILITIES.some(one => one.kind === stored) ? stored as ChartKind : "bar";
}
